import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Ghost } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { useLoads } from "@/hooks/useLoads";
import { useChangeRequests } from "@/hooks/useSlots";
import { auditRouteFor } from "@/lib/auditLinks";
import { statusLabel, statusTone } from "@/lib/statusBadge";
import { listAudit } from "@/services/api/audit";
import { getLoadCounters } from "@/services/api/loads";
import type { AuditLog } from "@/services/api/types";

/** Summarise one audit entry as "[actor] [action] [entity]: [from] > [to]". */
function auditSummary(entry: AuditLog): string {
  const actor = entry.actor_email ?? (entry.actor_user_id ? `#${entry.actor_user_id}` : "system");
  const action = entry.action;
  const entity = entry.entity_type;
  if (entry.action === "insert") {
    const pairs = entry.changes
      .slice(0, 4)
      .map((c) => `${c.field_name}=${c.new_value ?? ""}`)
      .join(", ");
    return `${actor} ${action} ${entity}${pairs ? `: ${pairs}` : `: id ${entry.entity_id}`}`;
  }
  if (entry.action === "delete") {
    return `${actor} ${action} ${entity}: id ${entry.entity_id}`;
  }
  const diffs = entry.changes
    .slice(0, 4)
    .map((c) => `${c.field_name}: ${c.old_value ?? "—"} > ${c.new_value ?? "—"}`)
    .join(", ");
  return `${actor} ${action} ${entity}${diffs ? `: ${diffs}` : ""}`;
}

/** Main admin summary: recent activity, approvals and outstanding loads. */
export function SummaryPage() {
  const { t } = useTranslation();
  const loads = useLoads({ limit: 1 });
  const approvals = useChangeRequests("pending");
  const counters = useQuery({ queryKey: ["load-counters"], queryFn: () => getLoadCounters() });
  const audit = useQuery({
    queryKey: ["audit-recent"],
    queryFn: () => listAudit({ limit: 8, offset: 0 }),
  });
  // Entities without a record page (bay_schedule, import rows, …) have no
  // deep-link target; their entry opens an in-place ghost snapshot instead.
  const [ghost, setGhost] = useState<{ entity_type: string; entity_id: string | null } | null>(
    null,
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t("admin.summary")}</h1>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">{t("nav.loads")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">
              {loads.isLoading ? "…" : (counters.data?.total ?? loads.data?.total ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">{t("admin.lockedLoadsFuture")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">
              {counters.isLoading ? "…" : (counters.data?.locked_future ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">{t("admin.unlockedLoadsFuture")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">
              {counters.isLoading ? "…" : (counters.data?.unlocked_future ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">{t("admin.pendingApprovals")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{approvals.isLoading ? "…" : (approvals.data?.length ?? 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium">{t("admin.recentAudit")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{audit.isLoading ? "…" : (audit.data?.total ?? 0)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">{t("admin.recentAudit")}</CardTitle>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/audit">{t("nav.audit")}</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {audit.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (audit.data?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <ul className="divide-y">
              {audit.data?.items.map((entry) => {
                // Deep link carries the action + changed fields so the target
                // page can highlight what changed (ghost copy when deleted).
                const route = auditRouteFor(entry, entry.id);
                return (
                  <li key={entry.id} className="py-2 text-sm">
                    <div className="text-xs text-muted-foreground">
                      {new Date(entry.occurred_at).toLocaleString()}
                    </div>
                    <div className="mt-0.5">
                      {auditSummary(entry)}
                      {route ? (
                        <Link
                          to={route}
                          className="ml-2 text-xs text-primary hover:underline"
                        >
                          {t("admin.view")} →
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setGhost({
                              entity_type: entry.entity_type,
                              entity_id: entry.entity_id,
                            })
                          }
                          title={t("admin.auditSnapshot")}
                          className="ml-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          {t("admin.view")} <Ghost className="h-3 w-3" aria-hidden />
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">{t("loadboard.status")}</CardTitle>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/loads">{t("nav.loads")}</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {(loads.data?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No loads recorded yet.</p>
          ) : (
            <ul className="divide-y">
              {loads.data?.items.map((load) => (
                <li key={load.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="font-medium">{load.load_ref}</span>
                  <Badge variant={statusTone(load.status) as BadgeProps["variant"]}>
                    {statusLabel(load.status)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Fallback snapshot for entities without a record page. */}
      <Dialog open={ghost !== null} onOpenChange={(open) => !open && setGhost(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("admin.auditSnapshot")}</DialogTitle>
            <DialogDescription>{t("admin.auditSnapshotHint")}</DialogDescription>
          </DialogHeader>
          {ghost ? (
            <AuditGhostRecord
              entityType={ghost.entity_type}
              entityId={ghost.entity_id}
              variant="snapshot"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
